import { useEffect, useState } from 'react';
import {
  formatAbsoluteDate,
  formatAbsoluteTime,
  formatAbsoluteDateTime,
  formatRelativeTime,
} from './formatRelativeTime';

type Props = {
  value: string | Date | null | undefined;
  className?: string;
};

const TICK_MS = 30_000;

export const RelativeTime = ({ value, className }: Props) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!value) return;
    const id = window.setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [value]);

  if (!value) {
    return <em className={className}>Never</em>;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return <em className={className}>Unknown</em>;
  }

  return (
    <span
      className={[
        // `border-0` first: neither frontend loads Tailwind preflight, and
        // `border-dotted` styles ALL sides — without zeroing them the UA default
        // `medium` width turns the intended underline into a dotted box.
        'group relative inline-flex cursor-help items-center border-0 border-b border-dotted border-[rgba(255,255,255,0.28)] outline-none',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <time
        className="not-italic text-inherit"
        dateTime={date.toISOString()}
        aria-label={formatAbsoluteDateTime(value)}
      >
        {formatRelativeTime(value)}
      </time>
      <span
        className="pointer-events-none invisible absolute bottom-[calc(100%+8px)] left-1/2 z-30 flex min-w-[120px] -translate-x-1/2 flex-col items-center gap-0.5 whitespace-nowrap rounded-[8px] border border-border bg-[rgba(10,14,24,0.96)] px-3 py-2 opacity-0 shadow-[var(--shadow-popover)] transition-[opacity,visibility] duration-150 after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-[5px] after:border-transparent after:border-t-[rgba(10,14,24,0.96)] after:content-[''] group-hover:visible group-hover:opacity-100 motion-reduce:transition-none"
        role="tooltip"
      >
        <span className="text-xs font-semibold leading-[1.3] text-foreground">
          {formatAbsoluteDate(value)}
        </span>
        <span className="text-[11px] font-medium leading-[1.3] tabular-nums text-muted-foreground">
          {formatAbsoluteTime(value)}
        </span>
      </span>
    </span>
  );
};
